import "whatwg-fetch";
import React from "react";
import ReactDOM from "react-dom";
import { EmbedForm } from '@/domains/jobs';
import moment from "moment";
import { Modal } from "react-bootstrap";

import { dotStream } from "ldrs";

dotStream.register();

// Types
interface Job {
  id: number;
  job_type: {
    title: string;
    group?: string;
  };
  practice: {
    name: string;
    avatar_url?: string;
  };
  position_type: 'fulltime' | 'parttime' | 'temp' | 'longtermtemp';
  temp_date?: string;
  published_at: string;
  practice_id: number;
}

interface Practice {
  id: number;
  name: string;
  avatar_url?: string;
}

interface JobFormTags {
  job?: any;
  practices?: Practice[];
  skills?: any[];
  job_skills?: any[];
  job_types?: any[];
  position_types?: any[];
  form_elements?: any;
  skill_categories?: any[];
  authenticity_token?: string;
}

interface RequestProfessionalProps {
  authenticity_token: string;
  filter_by_job_type_id?: boolean;
  job_type_group?: string[];
  skills?: any[];
  job?: any;
  profile_id: number;
  is_temp_job?: boolean;
  jobFormTags?: JobFormTags;
  onRequestSent?: (jobId: string, isTemp: boolean) => void;
  buttonClassName?: string;
  buttonText?: string;
  disabled?: boolean;
}

interface RequestProfessionalState {
  modalOpen: boolean;
  newJob: boolean;
  jobs: Job[];
  existingJob: string | false;
  requested_job_id?: string;
  processing: boolean;
  error: boolean;
  requestSent?: boolean;
  errorMessage?: string;
}

interface ModalWindowProps {
  jobs: Job[];
  existingJob: string | false;
  setJob: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  requestTemp: () => void;
  openNewJob: () => void;
  temp: boolean;
  practices?: Practice[];
  resetForm?: () => void;
}

interface ExistingJobSelectProps {
  jobs: Job[];
  existingJob: string | false;
  setJob: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  practices?: Practice[];
}

interface JobOptionProps {
  job: Job;
}

interface ApiResponse {
  perm_jobs?: Job[];
  temp_jobs?: Job[];
  error?: string;
}

interface RequestResponse {
  success?: boolean;
  error?: string;
  message?: string;
}

// Configuration
const REQUEST_CONFIG = {
  POSITION_TYPES: {
    fulltime: 'Full Time',
    parttime: 'Part Time',
    temp: 'Temp',
    longtermtemp: 'Long Term Temp',
  },
  MODAL_SIZES: {
    small: 'modal-sm',
    medium: '',
    large: 'modal-lg',
  },
  LOADING_STATES: {
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
    IDLE: 'idle',
  },
  API_ENDPOINTS: {
    JOBS: '/jobs/manage.json',
    REQUEST: (jobId: string) => `/jobs/${jobId}/request.json`,
    INVITE: (jobId: string) => `/jobs/${jobId}/invite.json`,
  },
} as const;

// Utility Functions
const ReadablePositionType = (positionType: string): string => {
  return REQUEST_CONFIG.POSITION_TYPES[positionType as keyof typeof REQUEST_CONFIG.POSITION_TYPES] || positionType;
};

const formatJobDate = (job: Job): string => {
  const date = job.position_type === 'temp' ? job.temp_date : job.published_at;
  return moment(date).format("MMM Do YYYY");
};

// API Functions
const fetchJobs = async (
  isTemp: boolean,
  profileId: number,
  filterByJobTypeId?: boolean,
  jobTypeGroup?: string[]
): Promise<Job[]> => {
  try {
    const params = new URLSearchParams({
      no_closed: '1',
      profile_id: profileId.toString(),
    });

    if (isTemp) {
      params.append('temp', '1');
    }

    const response = await fetch(`${REQUEST_CONFIG.API_ENDPOINTS.JOBS}?${params}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch jobs: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse = await response.json();

    if (result.error) {
      throw new Error(result.error);
    }

    let jobs = isTemp ? (result.temp_jobs || []) : (result.perm_jobs || []);

    // Apply filtering if specified
    if (filterByJobTypeId && jobTypeGroup && jobTypeGroup.length > 0) {
      jobs = jobs.filter(job =>
        job.job_type?.group && jobTypeGroup.includes(job.job_type.group)
      );
    }

    return jobs;
  } catch (error) {
    console.error('Error fetching jobs:', error);
    throw error;
  }
};

const submitRequest = async (
  jobId: string,
  profileId: number,
  authenticityToken: string,
  isTemp: boolean
): Promise<RequestResponse> => {
  try {
    const endpoint = isTemp
      ? REQUEST_CONFIG.API_ENDPOINTS.REQUEST(jobId)
      : REQUEST_CONFIG.API_ENDPOINTS.INVITE(jobId);

    const body = isTemp
      ? { temp_requests: [profileId.toString()] }
      : { invitees: [profileId.toString()] };

    const response = await fetch(endpoint, {
      method: "POST",
      redirect: "manual",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status} ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error submitting request:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

// Sub-components
const JobOption: React.FC<JobOptionProps> = React.memo(({ job }) => (
  <option value={job.id.toString()}>
    {job.job_type.title} at {job.practice.name} (
    {ReadablePositionType(job.position_type)}, {formatJobDate(job)})
  </option>
));

JobOption.displayName = 'JobOption';

const ExistingJobSelect: React.FC<ExistingJobSelectProps> = React.memo(({
  jobs,
  existingJob,
  setJob,
  practices = []
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const selectedJob = jobs.find(job => job.id.toString() === existingJob);

  const filteredJobs = jobs.filter(job =>
    job.job_type.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.practice.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update dropdown position
  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  // Update position when opened and on scroll
  React.useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectJob = (jobId: string) => {
    setJob({ target: { value: jobId } } as any);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="form-group mb-4">
      <label className="form-label fw-semibold mb-3 d-block">
        <i className="fas fa-briefcase me-2 text-primary" aria-hidden="true"></i>
        Select a job posting
      </label>

      {/* Dropdown Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        className="form-select form-select-lg shadow-sm text-start d-flex align-items-center justify-content-between"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          borderRadius: '0.5rem',
          border: '2px solid #e0e0e0',
          padding: '0.75rem 1rem',
          fontSize: '1rem',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          background: 'white',
          width: '100%',
          minHeight: '3rem'
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {selectedJob ? (
          <div className="d-flex align-items-center flex-grow-1">
            <div className="flex-grow-1">
              <div className="fw-semibold">{selectedJob.job_type.title}</div>
              <small className="text-muted">{selectedJob.practice.name}</small>
            </div>
          </div>
        ) : (
          <span className="text-muted">
            <i className="fas fa-search me-2" aria-hidden="true"></i>
            Choose a job posting...
          </span>
        )}
        <i
          className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-2`}
          style={{
            transition: 'transform 0.2s ease',
            fontSize: '0.875rem'
          }}
          aria-hidden="true"
        ></i>
      </button>

      {/* Dropdown Panel - Rendered via Portal */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="dropdown-panel shadow-lg"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            background: 'white',
            borderRadius: '0.75rem',
            border: '2px solid #e0e0e0',
            zIndex: 9999,
            maxHeight: '450px',
            display: 'flex',
            flexDirection: 'column',
            opacity: 1,
            transform: 'translateY(0)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
          }}
        >
          {/* Search Input */}
          {jobs.length > 3 && (
            <div className="p-3 border-bottom" style={{ borderColor: '#f0f0f0' }}>
              <div className="input-group">
                <span className="input-group-text bg-light border-0">
                  <i className="fas fa-search text-muted" aria-hidden="true"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-light"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    fontSize: '0.95rem',
                    padding: '0.5rem 0.75rem'
                  }}
                />
              </div>
            </div>
          )}

          {/* Job Cards */}
          <div
            className="job-cards-container"
            style={{
              overflowY: 'auto',
              padding: '0.5rem',
              gap: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: jobs.length > 3 ? '350px' : '400px'
            }}
          >
            {filteredJobs.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-search fa-2x mb-2" aria-hidden="true"></i>
                <p>No jobs found matching "{searchTerm}"</p>
              </div>
            ) : (
              filteredJobs.map((job) => {
                const isSelected = existingJob === job.id.toString();
                return (
                  <div
                    key={job.id}
                    className={`job-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectJob(job.id.toString())}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectJob(job.id.toString());
                      }
                    }}
                    style={{
                      padding: '1rem 1.25rem',
                      borderRadius: '0.75rem',
                      border: isSelected ? '2px solid #0d6efd' : '2px solid #e0e0e0',
                      background: isSelected
                        ? 'linear-gradient(135deg, #f0f7ff 0%, #e3f2ff 100%)'
                        : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected
                        ? '0 4px 12px rgba(13, 110, 253, 0.15)'
                        : '0 2px 4px rgba(0,0,0,0.05)',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#0d6efd';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(13, 110, 253, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                      }
                    }}
                  >
                    {/* Selected Indicator */}
                    {isSelected && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '0.75rem',
                          right: '0.75rem',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#0d6efd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      >
                        <i className="fas fa-check" aria-hidden="true"></i>
                      </div>
                    )}

                    {/* Job Title & Practice */}
                    <div className="d-flex align-items-start mb-2">
                      <div className="flex-grow-1" style={{ paddingRight: '2rem' }}>
                        <h6 className="mb-1 fw-bold" style={{ color: isSelected ? '#0d6efd' : '#212529', fontSize: '1.1rem' }}>
                          {job.job_type.title}
                        </h6>
                        <div className="d-flex align-items-center text-muted" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-building me-2" aria-hidden="true"></i>
                          <span>{job.practice.name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Job Details */}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <span
                        className="badge"
                        style={{
                          background: isSelected ? '#0d6efd' : '#f8f9fa',
                          color: isSelected ? 'white' : '#6c757d',
                          padding: '0.4rem 0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-clock me-1" aria-hidden="true"></i>
                        {ReadablePositionType(job.position_type)}
                      </span>
                      <span
                        className="badge"
                        style={{
                          background: isSelected ? '#0d6efd' : '#f8f9fa',
                          color: isSelected ? 'white' : '#6c757d',
                          padding: '0.4rem 0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-calendar-alt me-1" aria-hidden="true"></i>
                        {formatJobDate(job)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Helper Text */}
      <small className="form-text text-muted mt-2 d-block">
        <i className="fas fa-info-circle me-1" aria-hidden="true"></i>
        {selectedJob ? `Selected: ${selectedJob.job_type.title}` : 'Choose a job to invite this candidate'}
      </small>
    </div>
  );
});

ExistingJobSelect.displayName = 'ExistingJobSelect';

const ExistingJobButton: React.FC<{ requestTemp: () => void; disabled?: boolean }> = React.memo(({
  requestTemp,
  disabled = false
}) => (
  <button
    type="button"
    className="btn btn-primary btn-lg shadow-sm"
    style={{
      borderRadius: '0.5rem',
      padding: '0.75rem 2rem',
      fontSize: '1rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.6 : 1
    }}
    onClick={requestTemp}
    disabled={disabled}
    aria-label="Send request for selected job"
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '';
    }}
  >
    <i className="fa fa-paper-plane me-2" aria-hidden="true"></i>
    Send Request
  </button>
));

ExistingJobButton.displayName = 'ExistingJobButton';

const NewJobButton: React.FC<{ openNewJob: () => void }> = React.memo(({ openNewJob }) => (
  <button
    type="button"
    className="btn btn-outline-primary btn-lg shadow-sm"
    style={{
      borderRadius: '0.5rem',
      padding: '0.75rem 2rem',
      fontSize: '1rem',
      fontWeight: '600',
      transition: 'all 0.2s ease'
    }}
    onClick={openNewJob}
    aria-label="Create a new job"
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '';
    }}
  >
    <i className="fa fa-plus me-2" aria-hidden="true"></i>
    Create a New Job
  </button>
));

NewJobButton.displayName = 'NewJobButton';

const ModalWindow: React.FC<ModalWindowProps> = React.memo(({
  jobs,
  existingJob,
  setJob,
  requestTemp,
  openNewJob,
  temp,
  practices = [],
}) => {
  const hasJobs = jobs.length > 0;
  const canSubmit = hasJobs && existingJob;

  return (
    <div className="modal-content-wrapper">
      {!temp && (
        <div className="alert alert-info mb-3" role="alert">
          <i className="fas fa-info-circle me-2" aria-hidden="true"></i>
          We will instantly send this candidate a direct message inviting them to apply!
        </div>
      )}

      {hasJobs ? (
        <div className="existing-jobs-section">
          <ExistingJobSelect
            jobs={jobs}
            existingJob={existingJob}
            setJob={setJob}
            practices={practices}
          />
          <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center mt-3">
            <ExistingJobButton
              requestTemp={requestTemp}
              disabled={!canSubmit}
            />
            <span className="text-muted align-self-center d-none d-sm-inline">or</span>
            <NewJobButton openNewJob={openNewJob} />
          </div>
        </div>
      ) : (
        <div className="no-jobs-section text-center">
          <div className="alert alert-warning mb-3" role="alert">
            <h5 className="alert-heading">No Jobs Available</h5>
            <p className="mb-0">
              Create a job to {temp ? "request this temp" : "invite this candidate"}.
            </p>
          </div>
          <NewJobButton openNewJob={openNewJob} />
        </div>
      )}
    </div>
  );
});

ModalWindow.displayName = 'ModalWindow';

/**
 * RequestProfessional Component
 * 
 * A comprehensive job request and invitation system for professionals.
 * Handles both temporary job requests and permanent position invitations
 * with full job creation workflow integration.
 * 
 * Features:
 * - Job selection from existing jobs
 * - New job creation workflow
 * - Temporary and permanent position support
 * - Loading states and error handling
 * - Modal-based interface
 * - Accessibility support
 * - TypeScript safety
 * 
 * Capabilities:
 * - Filter jobs by type and group
 * - Handle authentication tokens
 * - Optimistic UI updates
 * - Error recovery
 * - Success callbacks
 */
const RequestProfessional: React.FC<RequestProfessionalProps> = React.memo(({
  authenticity_token,
  filter_by_job_type_id = false,
  job_type_group = [],
  job,
  profile_id,
  is_temp_job = true,
  jobFormTags = {},
  onRequestSent,
  buttonClassName = "btn btn-primary btn-rounded col-12",
  buttonText,
  disabled = false
}) => {
  const [state, setState] = React.useState<RequestProfessionalState>({
    modalOpen: false,
    newJob: false,
    jobs: [],
    existingJob: false,
    requested_job_id: undefined,
    processing: true,
    error: false,
    requestSent: false,
    errorMessage: undefined,
  });

  const {
    requested_job_id,
    jobs,
    existingJob,
    newJob,
    processing,
    requestSent,
    modalOpen,
    error,
    errorMessage
  } = state;

  // Load jobs when component mounts or parameters change
  const loadJobs = React.useCallback(async () => {
    setState(prev => ({ ...prev, processing: true, error: false, errorMessage: undefined }));

    try {
      const fetchedJobs = await fetchJobs(
        is_temp_job,
        profile_id,
        filter_by_job_type_id,
        job_type_group
      );

      setState(prev => ({
        ...prev,
        jobs: fetchedJobs,
        existingJob: fetchedJobs.length > 0 ? fetchedJobs[0].id.toString() : false,
        processing: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        processing: false,
        error: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load jobs'
      }));
    }
  }, [is_temp_job, profile_id, filter_by_job_type_id, job_type_group]);

  React.useEffect(() => {
    if (modalOpen) {
      loadJobs();
    }
  }, [modalOpen, loadJobs]);

  // Modal handlers
  const openModal = React.useCallback(() => {
    setState(prev => ({ ...prev, modalOpen: true }));
  }, []);

  const closeModal = React.useCallback(() => {
    setState(prev => ({ ...prev, modalOpen: false }));
  }, []);

  // Job creation handlers
  const openNewJob = React.useCallback(() => {
    setState(prev => ({ ...prev, newJob: true }));
  }, []);

  const resetForm = React.useCallback(() => {
    setState({
      modalOpen: true,
      newJob: false,
      jobs: [],
      existingJob: false,
      requested_job_id: undefined,
      processing: true,
      error: false,
      requestSent: false,
      errorMessage: undefined,
    });
    // Trigger job reload after resetting form state
    loadJobs();
  }, [loadJobs]);

  // Job selection handler
  const setJob = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setState(prev => ({
      ...prev,
      existingJob: event.target.value,
    }));
  }, []);

  // Request submission handler
  const handleSubmitRequest = React.useCallback(async () => {
    if (!existingJob) return;

    setState(prev => ({ ...prev, processing: true, error: false, errorMessage: undefined }));

    try {
      const result = await submitRequest(
        existingJob,
        profile_id,
        authenticity_token,
        is_temp_job
      );

      if (result.success) {
        // Notify parent component and show success state
        onRequestSent?.(existingJob, is_temp_job);
        setState(prev => ({ ...prev, processing: false, requestSent: true }));
      } else {
        throw new Error(result.error || 'Request failed');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        processing: false,
        error: true,
        errorMessage: error instanceof Error ? error.message : 'Request failed'
      }));
    }
  }, [existingJob, profile_id, authenticity_token, is_temp_job, onRequestSent]);

  // Embed form options for job creation
  const embedFormOptions = React.useMemo(() => ({
    job,
    practices: jobFormTags.practices || [],
    skills: jobFormTags.skills || [],
    job_skills: jobFormTags.job_skills || [],
    job_types: jobFormTags.job_types || [],
    position_types: jobFormTags.position_types || [],
    form_elements: jobFormTags.form_elements || {},
    skill_categories: jobFormTags.skill_categories || [],
    authenticity_token,
    profile_id,
    temp: is_temp_job,
  }), [job, jobFormTags, authenticity_token, profile_id, is_temp_job]);

  const defaultButtonText = is_temp_job ? "Request to Temp" : "Invite to Apply";
  const modalTitle = is_temp_job ? "Request for Temp" : "Invite to Apply";

  return (
    <div className="request-professional-container">
      {/* Trigger Button */}
      <button
        type="button"
        className={buttonClassName}
        onClick={openModal}
        disabled={disabled}
        aria-label={buttonText || defaultButtonText}
      >
        {is_temp_job ? (
          <i className="fa fa-calendar-alt me-2" aria-hidden="true"></i>
        ) : (
          <i className="fa fa-paper-plane me-2" aria-hidden="true"></i>
        )}
        {buttonText || defaultButtonText}
      </button>

      {/* Modal */}
      <Modal
        show={modalOpen}
        onHide={closeModal}
        centered
        size="lg"
        aria-labelledby="request-modal-title"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title id="request-modal-title">
            {modalTitle}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* New Job Form */}
          {newJob && (
            <div className="new-job-section">
              <EmbedForm
                {...embedFormOptions}
                onSuccess={(job) => {
                  if (job && job.id) {
                    window.location.href = `/jobs/${job.id}`;
                  }
                }}
              />
            </div>
          )}

          {/* Loading State */}
          {processing && !newJob && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading available jobs...</p>
            </div>
          )}

          {/* Error State */}
          {error && !processing && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error:</strong> {errorMessage || 'An unexpected error occurred.'}
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={loadJobs}
                >
                  <i className="fas fa-redo me-1" aria-hidden="true"></i>
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Success State */}
          {requestSent && (
            <div className="text-center success-section">
              <div className="alert alert-success mb-4" role="alert">
                <i className="fas fa-check-circle me-2" aria-hidden="true"></i>
                <strong>Success!</strong> Your {is_temp_job ? 'request' : 'invitation'} has been sent.
              </div>

              {is_temp_job ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setState(prev => ({ ...prev, newJob: true, requestSent: false }))}
                >
                  <i className="fas fa-plus me-2" aria-hidden="true"></i>
                  Send Another Request
                </button>
              ) : (
                <div>
                  <h5 className="mb-3">Want to invite similar candidates to apply?</h5>
                  <a
                    href={`/jobs/${encodeURIComponent(requested_job_id || existingJob || '')}`}
                    className="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fas fa-users me-2" aria-hidden="true"></i>
                    View Matched Candidates
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Main Content */}
          {!newJob && !processing && !requestSent && !error && (
            <ModalWindow
              resetForm={resetForm}
              jobs={jobs}
              existingJob={existingJob}
              setJob={setJob}
              requestTemp={handleSubmitRequest}
              openNewJob={openNewJob}
              temp={is_temp_job}
              practices={jobFormTags.practices}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
});

RequestProfessional.displayName = 'RequestProfessional';

export default RequestProfessional;
export type {
  RequestProfessionalProps,
  Job as RequestJob,
  Practice as RequestPractice,
  JobFormTags as RequestJobFormTags
};
